import { useCallback, useEffect, useRef, useState } from "react";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Alert } from "@/components/ui/Alert";
import { Spinner } from "@/components/ui/Spinner";
import { Table } from "@/components/ui/Table";
import { TextInput, SelectInput } from "@/components/ui/Field";
import {
  createPrintLocation,
  deletePrintLocation,
  formatPrintJobFeedback,
  listPrintLocations,
  listPrinters,
  printTestPageAndWait,
  type PrintLocation,
} from "../../lib/settingsApi";
import { PrintQueueSection } from "./PrintQueueSection";
import { PrintPrinterDefaultsSection } from "./PrintPrinterDefaultsSection";
import { useRealtimeSync } from "@/lib/useRealtime";

type Feedback = {
  variant: "success" | "warning" | "error";
  title: string;
  message: string;
};

export function PrintLocationsSection() {
  const [locations, setLocations] = useState<PrintLocation[]>([]);
  const [printers, setPrinters] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [feedback, setFeedback] = useState<Feedback | null>(null);
  const [testingKey, setTestingKey] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [printer, setPrinter] = useState("");

  const reload = useCallback(async (options?: { silent?: boolean }) => {
    if (!options?.silent) setLoading(true);
    try {
      const [loc, pr] = await Promise.all([listPrintLocations(), listPrinters()]);
      setLocations(loc);
      setPrinters(pr);
      if (pr.length && !printer) setPrinter(pr[0] ?? "");
    } catch (err) {
      setFeedback({
        variant: "error",
        title: "Fehler",
        message: err instanceof Error ? err.message : "Druckstandorte konnten nicht geladen werden.",
      });
    } finally {
      setLoading(false);
    }
  }, [printer]);

  useEffect(() => {
    reload();
  }, [reload]);

  const reloadRef = useRef(reload);
  reloadRef.current = reload;
  useRealtimeSync({
    domains: ["print"],
    onChange: () => void reloadRef.current({ silent: true }),
  });

  async function handleAdd() {
    if (!name.trim() || !printer) return;
    try {
      await createPrintLocation(name.trim(), printer);
      setName("");
      setFeedback(null);
      await reload();
    } catch (err) {
      setFeedback({
        variant: "error",
        title: "Fehler",
        message: err instanceof Error ? err.message : "Anlegen fehlgeschlagen.",
      });
    }
  }

  async function handleTestPage(options: { printer?: string; locationId?: number; label: string }) {
    const key = options.locationId != null ? `loc:${options.locationId}` : `printer:${options.printer ?? ""}`;
    setTestingKey(key);
    setFeedback({
      variant: "warning",
      title: "Druck läuft",
      message: `Testseite für „${options.label}“ wird gedruckt und der Status geprüft…`,
    });
    try {
      const job = await printTestPageAndWait({
        printer: options.printer,
        locationId: options.locationId,
      });
      setFeedback(formatPrintJobFeedback(job));
    } catch (err) {
      setFeedback({
        variant: "error",
        title: "Druck fehlgeschlagen",
        message: err instanceof Error ? err.message : "Testdruck fehlgeschlagen.",
      });
    } finally {
      setTestingKey(null);
    }
  }

  return (
    <>
      <Card title="Standorte & Drucker" description="Druckstandorte mit zugeordnetem Drucker.">
        {loading && <Spinner />}
        {feedback && (
          <Alert variant={feedback.variant} title={feedback.title} className="mb-4 whitespace-pre-line">
            {feedback.message}
          </Alert>
        )}
        <div className="mb-4 flex flex-wrap gap-2">
          <TextInput placeholder="Standortname" value={name} onChange={(e) => setName(e.target.value)} />
          <SelectInput value={printer} onChange={(e) => setPrinter(e.target.value)}>
            {printers.map((p) => (
              <option key={p} value={p}>{p}</option>
            ))}
          </SelectInput>
          <Button type="button" onClick={handleAdd}>Hinzufügen</Button>
        </div>
        {!loading && (
          <Table<PrintLocation>
            rowKey={(r) => r.id}
            rows={locations}
            stickyEndColumnCount={1}
            columns={[
              {
                key: "name",
                header: "Standort",
                sortable: true,
                sortValue: (r) => r.name,
                render: (r) => r.name,
              },
              {
                key: "printer",
                header: "Drucker",
                sortable: true,
                sortValue: (r) => r.printer_name,
                render: (r) => r.printer_name,
              },
              {
                key: "actions",
                header: "",
                render: (r) => (
                  <div className="flex flex-wrap justify-end gap-2">
                    <Button
                      size="sm"
                      variant="secondary"
                      loading={testingKey === `loc:${r.id}`}
                      disabled={testingKey != null}
                      onClick={() =>
                        void handleTestPage({
                          locationId: r.id,
                          label: r.name || r.printer_name,
                        })
                      }
                    >
                      Testseite
                    </Button>
                    <Button
                      size="sm"
                      variant="danger"
                      disabled={testingKey != null}
                      onClick={async () => {
                        if (!window.confirm("Standort löschen?")) return;
                        await deletePrintLocation(r.id);
                        await reload();
                      }}
                    >
                      Löschen
                    </Button>
                  </div>
                ),
              },
            ]}
          />
        )}
      </Card>

      {!loading && <PrintQueueSection printers={printers} />}

      {!loading && printers.length > 0 && <PrintPrinterDefaultsSection printers={printers} />}

      {!loading && printers.length > 0 && (
        <Card
          title="Systemdrucker"
          description="Alle auf dem Server erkannten CUPS-Drucker – unabhängig von den Standorten."
          className="mt-4"
        >
          <Table<{ name: string }>
            rowKey={(r) => r.name}
            rows={printers.map((p) => ({ name: p }))}
            stickyEndColumnCount={1}
            columns={[
              {
                key: "name",
                header: "Drucker",
                sortable: true,
                sortValue: (r) => r.name,
                render: (r) => r.name,
              },
              {
                key: "actions",
                header: "",
                render: (r) => (
                  <div className="flex justify-end">
                    <Button
                      size="sm"
                      variant="secondary"
                      loading={testingKey === `printer:${r.name}`}
                      disabled={testingKey != null}
                      onClick={() =>
                        void handleTestPage({
                          printer: r.name,
                          label: r.name,
                        })
                      }
                    >
                      Testseite
                    </Button>
                  </div>
                ),
              },
            ]}
          />
        </Card>
      )}
    </>
  );
}
